import { forwardRef, useMemo } from 'react';

import { Check as CheckIcon } from '@design-system/icons';
import { ISOCountryCode } from '@foundation/countries';
import { cn } from '@foundation/utils';
import { CountryFlag } from '@shared/ui';

import {
  AdvancedSelect,
  AdvancedSelectOption,
  AdvancedSelectProps,
} from './AdvancedSelect';

export interface CountrySelectOption {
  countryCode: ISOCountryCode;
  label: string;
  extendedLabels?: string;
}

export interface CountrySelectProps
  extends Omit<AdvancedSelectProps<ISOCountryCode>, 'options'> {
  options: ReadonlyArray<CountrySelectOption>;
  /** Show flags in the option list. The selected country keeps its flag regardless. */
  showOptionFlags?: boolean;
}

const CountryTrigger = ({
  selectedOption,
  placeholder,
}: {
  selectedOption?: AdvancedSelectOption<ISOCountryCode>;
  placeholder?: string;
}) => {
  if (selectedOption) {
    return (
      <>
        <CountryFlag countryCode={selectedOption.value} />
        <span className="text-primary">{selectedOption.label}</span>
      </>
    );
  }
  if (placeholder) {
    return <span className="text-tertiary">{placeholder}</span>;
  }
  return null;
};

const createOptionRenderer =
  (showFlag: boolean) =>
  ({
    option,
    isSelected,
    isFocused,
    onSelect,
    onFocus,
    ref,
    'data-testid': dataTestId,
  }: {
    option: AdvancedSelectOption<ISOCountryCode> & {
      readonly extendedLabels?: string;
    };
    isSelected: boolean;
    isFocused: boolean;
    onSelect: (value: ISOCountryCode) => void;
    onFocus: () => void;
    ref: (el: HTMLButtonElement | null) => void;
    'data-testid'?: string;
  }) =>
    (
      <button
        ref={ref}
        type="button"
        onMouseDown={() => onSelect(option.value)}
        onFocus={() => onFocus()}
        className={cn(
          'w-full pl-6 pr-4 py-3 flex items-center gap-3 hover:bg-gray-100 transition-colors text-left focus:outline-none',
          isSelected && 'bg-alert-positive-primary',
          isFocused && 'bg-gray-200',
        )}
        data-testid={dataTestId ? dataTestId : undefined}
      >
        {showFlag && (
          <span className="shrink-0">
            <CountryFlag countryCode={option.value} />
          </span>
        )}
        <span className="text-primary">{option.label}</span>
        {option.extendedLabels && (
          <p className="text-gray-700">{option.extendedLabels}</p>
        )}
        {isSelected && (
          <span className="ml-auto self-center flex text-white mr-2 bg-alert-positive-500 rounded-full p-1">
            <CheckIcon size={16} />
          </span>
        )}
      </button>
    );

export const CountrySelect = forwardRef<HTMLInputElement, CountrySelectProps>(
  (
    {
      options,
      value,
      label,
      showOptionFlags = false,
      ...props
    }: CountrySelectProps,
    ref,
  ) => {
    const countries: AdvancedSelectOption<ISOCountryCode>[] = useMemo(
      () =>
        options.map(({ countryCode, label, extendedLabels }) => ({
          value: countryCode,
          label,
          extendedLabels,
          searchableText: `${label} ${extendedLabels ?? ''}`,
        })),
      [options],
    );

    const renderOption = useMemo(
      () => createOptionRenderer(showOptionFlags),
      [showOptionFlags],
    );

    return (
      <AdvancedSelect<ISOCountryCode>
        ref={ref}
        label={label}
        options={countries}
        value={value as ISOCountryCode}
        styles={{
          modal: 'h-[90vh] md:max-w-xl',
        }}
        renderTrigger={(selectedOption) => (
          <CountryTrigger
            selectedOption={selectedOption}
            placeholder={label}
          />
        )}
        renderOption={renderOption}
        getOptionKey={(option) => option.value}
        {...props}
      />
    );
  },
);

CountrySelect.displayName = 'CountrySelect';
